/**
 * Автозаполнение посевного плана — этап 2 по макету v2-15 (нижний блок).
 *
 * Два способа заполнить пустые поля разом:
 *   А. **По правилам** — «если предшественник X (и препредшественник Y) → сеем Z».
 *      Правила читаются и применяются СВЕРХУ ВНИЗ: первое подошедшее забирает
 *      поле, следующие его уже не видят. Так порядок в списке становится
 *      приоритетом — ровно то, чего ждёт человек, читающий список сверху.
 *   Б. **По севообороту** — кольцо культур, система ищет, где каждое поле на
 *      нём стоит, и предлагает следующую по кольцу.
 *
 * Общее правило обоих: **автозаполнение только ДОБАВЛЯЕТ**. Поля с уже
 * назначенной культурой и поля под паром оно не трогает — иначе одно нажатие
 * затирало бы работу, которую агроном делал руками полчаса.
 *
 * Перед применением — предпросмотр числами: сколько полей заденет каждое
 * правило и сколько заполнится всего. Массовое действие вслепую на две сотни
 * полей никто применять не станет, и правильно сделает.
 *
 * Ни правила, ни кольцо НЕ сохраняются: это разовая операция подбора (как в
 * макете). Хранение севооборота хозяйства — отдельная сущность со своей
 * моделью и миграцией, и заводить её «заодно» нельзя.
 */
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useCropPicker } from '@/components/fields/card/api';
import type { FillMethod } from '@/lib/planning/autoBuild';
import {
  useFillPlanBatches,
  useRotationMatch,
  type VariantField,
} from '@/lib/planning/planCrops';
import { formatHa } from '@/lib/planning/planVariant';
import { pickableCrops } from '../plan-variant/AddCropDialog';
import {
  bucketizeRotation,
  matchDepthText,
  previewRules,
  toFillBatches,
  yearsWord,
  type FillRule,
  type RotationMatch,
} from './autoFill';
import { ClearAutoRowsButton } from './ClearAutoRowsButton';
import { FillPlanMenu } from './FillPlanMenu';
import { TargetFillWizard } from './TargetFillWizard';
import { historyOptions } from './sowingPlan';

const ANY = '__any__';

/** Подпись выбранного способа рядом с кнопкой — состояние читается без клика. */
const METHOD_TITLE: Record<FillMethod, string> = {
  rules: 'по правилам',
  rotation: 'по севообороту',
  targets: 'по целям на культуры',
  last_season: 'как в прошлом сезоне',
};

const FALLOW_KEY = 'fallow';

let ruleSeq = 0;
const nextRuleId = () => `rule-${(ruleSeq += 1)}`;

// ── А. Правила ──────────────────────────────────────────────────────────────

function RulesSection({
  fields,
  scenarioId,
  crops,
  disabled,
}: {
  fields: VariantField[];
  scenarioId: string;
  crops: { id: string; name: string }[];
  disabled: boolean;
}) {
  const [rules, setRules] = useState<FillRule[]>([]);
  const [draft, setDraft] = useState<FillRule>({
    id: 'draft',
    predecessorCropId: null,
    predecessor2CropId: null,
    cropId: '',
  });
  const fill = useFillPlanBatches();

  const prevOptions = useMemo(() => historyOptions(fields, 'predecessor'), [fields]);
  const prev2Options = useMemo(() => historyOptions(fields, 'predecessor2'), [fields]);
  const preview = useMemo(() => previewRules(fields, rules), [fields, rules]);
  const cropName = (id: string) => crops.find((c) => c.id === id)?.name ?? 'культура';
  const historyName = (id: string | null, list: { id: string; name: string }[]) =>
    id === null ? 'любой' : (list.find((o) => o.id === id)?.name ?? 'культура');

  const addRule = () => {
    if (!draft.cropId) return;
    setRules((prev) => [...prev, { ...draft, id: nextRuleId() }]);
    setDraft({ id: 'draft', predecessorCropId: null, predecessor2CropId: null, cropId: '' });
  };

  const apply = () => {
    // Имя культуры едет в пачке: без него итог отказа сказал бы «не удалось на
    // культуре crop-8f3a…» вместо «на сое».
    const batches = toFillBatches(preview.assignment).map((b) => ({
      ...b,
      cropName: cropName(b.cropId),
    }));
    if (batches.length === 0 || fill.isPending) return;
    fill.mutate(
      { scenarioId, batches, source: 'По правилам' },
      {
        // Чистим правила только если всё легло: при частичном отказе список
        // нужен, чтобы повторить оставшееся, а не собирать его заново.
        onSuccess: (res) => {
          if (res.failed.length === 0) setRules([]);
        },
      },
    );
  };

  return (
    <Stack gap={3}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Если предшественник</Label>
          <Select
            value={draft.predecessorCropId ?? ANY}
            onValueChange={(v) =>
              setDraft((d) => ({ ...d, predecessorCropId: v === ANY ? null : v }))
            }
          >
            <SelectTrigger className="w-48" aria-label="Предшественник в правиле">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>любой</SelectItem>
              {prevOptions.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">и препредшественник</Label>
          <Select
            value={draft.predecessor2CropId ?? ANY}
            onValueChange={(v) =>
              setDraft((d) => ({ ...d, predecessor2CropId: v === ANY ? null : v }))
            }
          >
            <SelectTrigger className="w-48" aria-label="Препредшественник в правиле">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>любой</SelectItem>
              {prev2Options.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">→ сеем</Label>
          <Select
            value={draft.cropId || ANY}
            onValueChange={(v) => setDraft((d) => ({ ...d, cropId: v === ANY ? '' : v }))}
          >
            <SelectTrigger className="w-48" aria-label="Культура в правиле">
              <SelectValue placeholder="выберите культуру" />
            </SelectTrigger>
            <SelectContent>
              {crops.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button type="button" variant="outline" disabled={!draft.cropId} onClick={addRule}>
          Добавить правило
        </Button>
      </div>

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Правил пока нет. Добавьте первое — система сразу покажет, скольких полей оно
          коснётся, и только потом заполнит.
        </p>
      ) : (
        <Stack gap={2}>
          {rules.map((r, index) => {
            const row = preview.rows.find((p) => p.ruleId === r.id);
            return (
              <div
                key={r.id}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 text-sm"
              >
                <Badge variant="secondary">{index + 1}</Badge>
                <span className="text-muted-foreground">если предшественник</span>
                <span className="font-medium text-foreground">
                  {historyName(r.predecessorCropId, prevOptions)}
                </span>
                {r.predecessor2CropId && (
                  <>
                    <span className="text-muted-foreground">и препредшественник</span>
                    <span className="font-medium text-foreground">
                      {historyName(r.predecessor2CropId, prev2Options)}
                    </span>
                  </>
                )}
                <span className="text-muted-foreground">→ сеем</span>
                <span className="font-medium text-foreground">{cropName(r.cropId)}</span>
                <span className="ml-auto text-muted-foreground">
                  {row && row.fields > 0
                    ? `заденет полей: ${row.fields} · ${formatHa(row.areaHa)}`
                    : 'свободных полей не осталось'}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setRules((prev) => prev.filter((x) => x.id !== r.id))}
                >
                  Убрать
                </Button>
              </div>
            );
          })}

          <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-3">
            <span className="text-sm font-medium text-foreground">
              Заполнится {preview.totalFields} из {preview.freeFields} свободных полей ·{' '}
              {formatHa(preview.totalAreaHa)}
            </span>
            <Button
              type="button"
              size="sm"
              disabled={disabled || preview.totalFields === 0 || fill.isPending}
              onClick={apply}
            >
              {fill.isPending ? 'Заполняем…' : 'Применить правила'}
            </Button>
            <span className="text-xs text-muted-foreground">
              Правила применяются сверху вниз и только к полям без культуры.
            </span>
          </div>
        </Stack>
      )}
    </Stack>
  );
}

// ── Б. Севооборот ───────────────────────────────────────────────────────────

function RotationSection({
  scenarioId,
  crops,
  disabled,
}: {
  scenarioId: string;
  crops: { id: string; name: string }[];
  disabled: boolean;
}) {
  const [ring, setRing] = useState<string[]>([]);
  const [pick, setPick] = useState('');
  const [norms, setNorms] = useState<Map<string, number>>(new Map());
  const match = useRotationMatch<RotationMatch>(scenarioId, ring);
  const fill = useFillPlanBatches();

  const plan = useMemo(
    () => (match.data ? bucketizeRotation(match.data, norms) : null),
    [match.data, norms],
  );

  const ringName = (key: string) =>
    key === FALLOW_KEY ? 'Пар' : (crops.find((c) => c.id === key)?.name ?? 'культура');

  const apply = () => {
    if (!plan || fill.isPending) return;
    const batches = toFillBatches(plan.assignment).map((b) => ({
      ...b,
      cropName: ringName(b.cropId),
    }));
    if (batches.length === 0) return;
    fill.mutate({ scenarioId, batches, source: 'По севообороту' });
  };

  return (
    <Stack gap={3}>
      <div className="flex flex-wrap items-center gap-2">
        {ring.map((key, index) => (
          <div key={`${key}-${index}`} className="flex items-center gap-1">
            <Badge variant={key === FALLOW_KEY ? 'secondary' : 'primary'} className="gap-2">
              {index + 1}. {ringName(key)}
              <button
                type="button"
                aria-label={`Убрать ${ringName(key)} из севооборота`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setRing((prev) => prev.filter((_, i) => i !== index))}
              >
                ×
              </button>
            </Badge>
            <span className="text-muted-foreground">→</span>
          </div>
        ))}
        {ring.length > 0 && (
          <span className="text-xs text-muted-foreground">кольцо: снова 1. {ringName(ring[0])}</span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Select value={pick || ANY} onValueChange={(v) => setPick(v === ANY ? '' : v)}>
          <SelectTrigger className="w-56" aria-label="Культура для севооборота">
            <SelectValue placeholder="добавить культуру" />
          </SelectTrigger>
          <SelectContent>
            {/* Пар — позиция севооборота, но не культура: массово его не
                посеять, и об этом сказано в подборе. */}
            <SelectItem value={FALLOW_KEY}>Пар</SelectItem>
            {crops.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          disabled={!pick}
          onClick={() => {
            setRing((prev) => [...prev, pick]);
            setPick('');
          }}
        >
          + В севооборот
        </Button>
        {ring.length > 0 && (
          <Button type="button" variant="ghost" onClick={() => setRing([])}>
            Очистить
          </Button>
        )}
      </div>

      {ring.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Соберите севооборот по порядку — за последней культурой снова идёт первая.
          Система найдёт, где каждое поле стоит на этом кольце по своей истории, и
          предложит следующую культуру.
        </p>
      )}

      {match.isLoading && ring.length > 0 && <Skeleton className="h-32 w-full" />}

      {match.data && plan && (
        <Stack gap={3}>
          {match.data.return_norms.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Правила возврата:</span>
              {match.data.return_norms.map((n) => {
                const value = norms.get(n.crop_id) ?? n.min_return_years;
                return (
                  <span
                    key={n.crop_id}
                    className="flex items-center gap-1 rounded-full border border-accent-brand/40 bg-accent-brand/10 px-3 py-1 text-xs text-accent-brand"
                  >
                    {n.name} — не раньше чем через
                    <Input
                      className="h-6 w-12 px-1 text-center text-xs"
                      inputMode="numeric"
                      aria-label={`Норматив возврата: ${n.name}`}
                      value={String(value)}
                      onChange={(e) => {
                        const parsed = Number(e.target.value);
                        setNorms((prev) => {
                          const next = new Map(prev);
                          if (Number.isFinite(parsed) && parsed > 0) next.set(n.crop_id, parsed);
                          else next.delete(n.crop_id);
                          return next;
                        });
                      }}
                    />
                    {yearsWord(value)}
                  </span>
                );
              })}
              <span className="text-xs text-muted-foreground">
                нормативы из справочника культур; правка действует на это заполнение и в
                справочник не пишется
              </span>
            </div>
          )}

          <Bucket
            title="История совпадает полностью — культуру подставим сразу"
            tone="full"
            rows={plan.full}
            total={plan.totalFields}
          />
          <Bucket
            title="Совпадает частично — предложим с пометкой"
            tone="partial"
            rows={plan.partial}
            total={plan.totalFields}
          />
          <Bucket
            title="Совпадение слабое — оставим пустыми, заполните руками"
            tone="weak"
            rows={plan.weak}
            total={plan.totalFields}
          />

          <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/5 p-3">
            <span className="text-sm font-medium text-foreground">
              Заполнится {plan.assignment.size} из {plan.totalFields} свободных полей
            </span>
            <Button
              type="button"
              size="sm"
              disabled={disabled || plan.assignment.size === 0 || fill.isPending}
              onClick={apply}
            >
              {fill.isPending ? 'Заполняем…' : 'Применить севооборот'}
            </Button>
            <span className="text-xs text-muted-foreground">
              После заполнения всё правится руками в таблице выше.
            </span>
          </div>
        </Stack>
      )}
    </Stack>
  );
}

function Bucket({
  title,
  tone,
  rows,
  total,
}: {
  title: string;
  tone: 'full' | 'partial' | 'weak';
  rows: ReturnType<typeof bucketizeRotation>['full'];
  total: number;
}) {
  const [open, setOpen] = useState(false);
  const width = total > 0 ? Math.round((rows.length / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
          <div
            className={
              tone === 'full'
                ? 'h-full bg-primary'
                : tone === 'partial'
                  ? 'h-full bg-accent-brand'
                  : 'h-full bg-muted-foreground/40'
            }
            style={{ width: `${width}%` }}
          />
        </div>
        <span className="text-sm text-foreground">
          {rows.length} {rows.length === 1 ? 'поле' : 'полей'} — {title}
        </span>
        {rows.length > 0 && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
            {open ? 'Свернуть' : 'Показать поля'}
          </Button>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-1 rounded-md border border-border p-2">
          {rows.slice(0, 40).map((r) => (
            <div key={r.field.field_id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium text-foreground">{r.field.name}</span>
              <span className="text-muted-foreground">{formatHa(r.field.area_ha)}</span>
              <span className="text-muted-foreground">
                {r.field.history.map((h) => `${h.year} ${h.name.toLowerCase()}`).join(' · ') ||
                  'истории нет'}
              </span>
              <span className="ml-auto text-muted-foreground">{matchDepthText(r.field)}</span>
              {r.blockedReason ? (
                <span className="text-accent-brand">{r.blockedReason}</span>
              ) : (
                r.field.suggested_name && (
                  <Badge variant="primary">{r.field.suggested_name}</Badge>
                )
              )}
            </div>
          ))}
          {rows.length > 40 && (
            <span className="text-xs text-muted-foreground">
              …и ещё {rows.length - 40}: список обрезан, чтобы страница не встала
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Панель целиком ──────────────────────────────────────────────────────────

export function AutoFillPanel({
  scenarioId,
  fields,
  canSow,
  canRemove,
  variantStatus,
}: {
  scenarioId: string;
  fields: VariantField[];
  canSow: boolean;
  /** Право убирать посевы — под «Убрать автоматические» (SRS §2). */
  canRemove: boolean;
  /** Статус варианта: автосбор по целям живёт только в черновике (FR-2). */
  variantStatus: string;
}) {
  // Способ заполнения — один выбор на панель. До AK-791 это были три кнопки
  // рядом; founder 04.09 свёл их в одну «Заполнить план» с меню, потому что
  // намерение у агронома одно, а различается только способ.
  const [tab, setTab] = useState<FillMethod>('rules');
  const cropPicker = useCropPicker();
  const crops = useMemo(() => pickableCrops(cropPicker.data ?? []), [cropPicker.data]);
  const freeFields = useMemo(
    () => fields.filter((f) => !f.occupied_by).length,
    [fields],
  );
  const autoRows = useMemo(
    () => fields.filter((f) => f.plan_row_origin === 'auto').length,
    [fields],
  );
  // Пашня варианта — учётная площадь тех же полей, что кормят СПП и сводку:
  // знаменатель процентов цели обязан совпадать с ними до гектара.
  const arableHa = useMemo(
    () => fields.reduce((sum, f) => sum + (f.area_ha || 0), 0),
    [fields],
  );

  if (!canSow) {
    // Без права на посевы автозаполнение не показываем вовсе: панель, которая
    // умеет только считать предпросмотр и падать на применении, — обман.
    return null;
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Заполнить план</CardTitle>
        <p className="text-xs text-muted-foreground">
          Ручные назначения ни один способ не трогает. Уже назначенное и поля под паром
          остаются как есть — переписать вашу работу одним нажатием система не может.
        </p>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <FillPlanMenu
            method={tab}
            variantStatus={variantStatus}
            freeFields={freeFields}
            onPick={setTab}
          />
          <span className="text-xs text-muted-foreground">
            Способ: {METHOD_TITLE[tab]}
          </span>
          {canRemove && (
            <div className="ml-auto">
              <ClearAutoRowsButton
                scenarioId={scenarioId}
                autoRows={autoRows}
                disabled={variantStatus !== 'draft'}
              />
            </div>
          )}
        </div>

        {tab === 'rules' && (
          <RulesSection
            fields={fields}
            scenarioId={scenarioId}
            crops={crops}
            disabled={!canSow}
          />
        )}
        {tab === 'rotation' && (
          <RotationSection scenarioId={scenarioId} crops={crops} disabled={!canSow} />
        )}
        {tab === 'targets' && (
          <TargetFillWizard
            // Новый заход = новая форма: без ключа предыдущая цель и раскладка
            // залипли бы в уже закрытом мастере.
            key={`${scenarioId}:targets`}
            scenarioId={scenarioId}
            variantStatus={variantStatus}
            arableHa={arableHa}
            crops={crops}
            canSow={canSow}
            onClose={() => setTab('rules')}
          />
        )}
      </CardContent>
    </Card>
  );
}
